import { isDev } from 'src/environmentVariables/EnvironmentVariables';
import { getOracleUrl } from 'src/utils/oracle';
import UIHelpTextLink from '../UIHelpTextLink';
import {
	BigNum,
	OracleSource,
	PRICE_PRECISION_EXP,
	PublicKey,
	ZERO,
	millisFromStoredUnits,
	slotsToMsNum,
} from '@velocity-exchange/sdk';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useRiskCeilingSlotClock } from 'src/hooks/useSlotClock';
import { isPythOracle, isPullOracle } from '@velocity-exchange/common';
import { VELOCITY_DOCS_URL } from 'src/constants/constants';
import {
	MarketData,
	useLiveOracleForDisplay,
	useLiveRawOracleData,
} from 'src/stores/marketLiveState';
import { MarketId } from '@velocity-exchange/common';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';

/** Seconds, for copy that used to be denominated in slots. */
const formatSecondsAgo = (ms: number) =>
	`${(Math.max(ms, 0) / 1000).toFixed(1)}s`;

export const MarketOracleInfo = ({
	marketId,
	marketData,
	oracleSource,
	selectedMarketBaseAssetSymbol,
	oracle,
	oracleDelayText,
	confidenceIntervalTooLargeText,
}: {
	marketId: MarketId;
	marketData: MarketData;
	oracleSource: OracleSource;
	selectedMarketBaseAssetSymbol: string | undefined;
	oracle: PublicKey;
	oracleDelayText: string;
	confidenceIntervalTooLargeText: string;
}) => {
	return (
		<>
			Oracle provided by
			<a
				href={getOracleUrl(
					oracleSource,
					selectedMarketBaseAssetSymbol ?? '',
					oracle?.toString(),
					isDev()
				)}
				target="_blank"
				rel="noreferrer"
			>
				{' '}
				{isPythOracle(oracleSource) ? 'Pyth' : 'Switchboard'}
			</a>
			.
			<br />
			<MarketOracleInfoText
				marketId={marketId}
				marketData={marketData}
				oracleSource={oracleSource}
				oracleDelayText={oracleDelayText}
				confidenceIntervalTooLargeText={confidenceIntervalTooLargeText}
			/>
		</>
	);
};

const MarketOracleInfoText = ({
	marketId,
	marketData,
	oracleSource,
	oracleDelayText,
	confidenceIntervalTooLargeText,
}: {
	marketId: MarketId;
	marketData: MarketData;
	oracleSource: OracleSource;
	oracleDelayText: string;
	confidenceIntervalTooLargeText: string;
}) => {
	const velocityClient = useVelocityClient();
	const displayOracle = useLiveOracleForDisplay(marketId);
	const rawOracle = useLiveRawOracleData(marketId);
	const isMmOracle = displayOracle?.isMmOracle ?? false;
	const oraclePriceToUse = displayOracle?.price;

	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();
	const decimalsForMarket =
		selectedMarketInfo?.info?.genericInfo?.tickSizeExponent ?? 2;

	// The displayed price, its confidence error, and its slot-delay must all
	// describe the SAME oracle. useLiveOracleForDisplay picks whichever of the
	// market-maker / regular oracle has the fresher slot; the branches below read
	// that winner's own confidence and slot-delay so the three never mix sources.
	//
	// - mmOracle: confidence rides on the snapshot.
	// - regular oracle: confidence comes from the raw read, which borrows a real
	//   value from a raw-carrying (rpc/DLOB) transport when the winning one (the
	//   detail-less pricing-ws, price + slot only) ships none — otherwise the
	//   confidence warning would collapse to 0.
	const oracleConfidence = isMmOracle
		? displayOracle?.confidence
		: (rawOracle?.confidence ?? displayOracle?.confidence);
	const oracleErr =
		BigNum.from(oracleConfidence ?? ZERO, PRICE_PRECISION_EXP)?.toFixed?.(
			decimalsForMarket
		) ?? '0';

	const oracleSlotDelay = isMmOracle
		? marketData.lastMmOracleSlotDelay
		: marketData.lastOracleSlotDelay;

	// The staleness bound is the program's own, decoded from its legacy 400ms
	// storage units; the measured delay is a slot count, so it converts at the
	// live slot length. Without the state account there is no bound to judge
	// against, so the warning stays off rather than assuming one.
	const { slotDurationMs } = useRiskCeilingSlotClock();
	const staleForAmmMs = velocityClient
		? millisFromStoredUnits(
				velocityClient.getStateAccount().oracleGuardRails.validity
					.slotsBeforeStaleForAmm
			).toNumber()
		: undefined;
	const oracleDelayMs = slotsToMsNum(oracleSlotDelay, slotDurationMs);

	if (isPullOracle(oracleSource)) {
		return <>Last pull: {formatSecondsAgo(oracleDelayMs)} ago</>;
	}

	if (!oraclePriceToUse) {
		return null;
	}

	const oraclePriceNum = oraclePriceToUse.toNum();

	return (
		<>
			{staleForAmmMs !== undefined && oracleDelayMs >= staleForAmmMs ? (
				<>
					{'⚠️ Oracle Status: Delayed/Stale Data (Updated ' +
						formatSecondsAgo(oracleDelayMs) +
						' ago)'}
					<br /> <br />
					{oracleDelayText + ' The order matching is still available.'}
					<UIHelpTextLink
						href={`${VELOCITY_DOCS_URL}/trading/oracles`}
						text="More Info..."
						className="mt-2"
					/>
				</>
			) : parseFloat(oracleErr) / oraclePriceNum >= 0.02 ? (
				<>
					{'⚠️ Oracle Status: Confidence Interval Too Large (Updated ' +
						`${((parseFloat(oracleErr) / oraclePriceNum) * 100).toFixed(2)}` +
						'% )'}
					<br /> <br />
					{confidenceIntervalTooLargeText +
						' The order matching is still available.'}
					<UIHelpTextLink
						href={`${VELOCITY_DOCS_URL}/trading/oracles`}
						text="More Info..."
						className="mt-2"
					/>
				</>
			) : (
				''
			)}
		</>
	);
};
